/**
 * http 请求类，继承拦截处理
 * opts = {
 *      baseURL: string 基础URL地址
 *      timeout: number 超时时间
 *      token： string 每次请求token
 *      requestHandle function(config):config 请求处理函数，返回处理后的config
 *      responseHandle function(response): response 响应处理函数，返回处理后的response
 * }
 *
 */

import axios from 'axios';
import qs from 'qs';
import {
    hexMD5
} from './md5.js'

class Http {
    constructor(opts) {
        const baseOptions = {
            baseURL: '',
            timeout: 20000,
            requestHandle: null,
            responseHandle: null
        };

        // 生成新的配置参数
        const options = Object.assign(baseOptions, opts);

        // http 实例
        this.http = axios.create({
            baseURL: options.baseURL,
            timeout: options.timeout
        });


        // 请求拦截器
        this.http.interceptors.request.use(
            (config) => {
                if (options.requestHandle) {
                    config = options.requestHandle(config)
                }
                return config;
            },
            (error) => {
                return error;
            }
        );

        // 响应拦截
        this.http.interceptors.response.use(
            (response) => {
                if (options.responseHandle) {
                    response = options.responseHandle(response);
                }
                return Http.parseResponse(response);
            },
            (error) => {
                let response = error.response;
                if (options.responseHandle) {
                    response = options.responseHandle(response, error);
                }
                return Http.parseResponse(response, error);
            }
        )
    }


    /**
     * 请求状态
     * @param response axios response
     * @param response axios error
     * @return {*}
     *
     */
    static parseResponse(response, error = null) {
        if (response) {
            return {
                status: response.status === 200 || response.status === 201,
                data: response.data
            }
        } else if (error) {
            return {
                status: false,
                data: error.message
            }
        }
    }

    /**
     * 解析URL
     * @param url <string> '/aaa/:id'
     * @param routeParam = <object|map> {id:123} 将此对象的属性替换url中参数
     * @param queryParam = <object|map> {name: 'abc', attr: 'def} url后面查询参数
     * @return {string}
     *
     */
    parseUrl(url, {
        routeParam,
        queryParam
    } = {
        routeParam: {},
        queryParam: {}
    }) {
        if (routeParam) {
            url = url.replace(/:([^/]+)/g, (match, keyName) => {
                return routeParam[keyName];
            });
        }

        if (queryParam) {
            const queryString = [];
            for (const i in queryParam) {
                queryString.push(`${i}=${queryParam[i]}`);
            }
            url += '?' + queryString.join('&');
        }
        return url;
    }
    /**
     * 对象转参数串
     */
    serializeObject(params) {
        if (params) {
            let result = '?'
            for (let key in params) {
                // 添加时间戳IE浏览器缓存问题，encodeURIComponent（get汉字提交处理）
                result += key + '=' + encodeURIComponent(params[key]) + '&'
            }
            return result.substr(0, result.length - 1)
        }
        return null
    }

    /**
     * GET 请求
     * @param url <string> '/aa/:id'
     * @param <object|map> {
     *  routeParam = {id: 232} 替换地址中的参数
     *  queryParam = {name: 'abc', attr: 'def'} url后面查询参数
     * }
     * 解析后地址 url= '/aa/232?name=abc&attr=def'
     *  @return {Promise<*>}
     *
     */
    get(url, param = null, config = null) {
        if (param) {
            url = url + this.serializeObject(param);
        }
        return this.http.get(url, config);
    }

    delete(url, param = null, config = null) {
        if (param) {
            url = this.parseUrl(url, param);
        }
        return this.http.delete(url, config);
    }

    post(url, data = null, param = null, config = null) {
        if (param) {
            url = this.parseUrl(url, param);
        }

        let dataPara = {}
        dataPara = {
            ...(data || {}),
        }
        // const token = localStorage.getItem('token');
        // if (token) {
        //     dataPara['token'] = token;
        // }
        return this.http.post(url, dataPara, config);
    }
    postArr(url, data = null, param = null, config = null) {
        if (param) {
            url = this.parseUrl(url, param);
        }

        // let dataPara = {}
        // dataPara = {
        //     ...(data || {}),
        // }
        // const token = localStorage.getItem('token');
        // if (token) {
        //     dataPara['token'] = token;
        // }
        return this.http.post(url, data, config);
    }
    postGet(url, data = null, params = null, config = null) {
        if (params) {
            url = url + this.serializeObject(params);
        }

        return this.http.post(url, data, config);
    }
    postList(url, data = null, param = null, config = null) {
        if (param) {
            url = this.parseUrl(url, param);
        }

        return this.http.post(url, data, config);
    }
    postUpload(url, data = null, param = null, config = null) {
        let urls = url
        let dataPara = {}
        dataPara = {
            ...(data || {}),
        }
        config = {
            responseType: 'blob'
        }
        return this.http.post(urls, dataPara, config)
    }
    postFile(url, data = null, param = null, config = null) {
        let urls = url
        const form = new FormData();
        let dataPara = {}
        dataPara = {
            ...(data || {}),
        }
        const token = localStorage.getItem('token');
        if (token) {
            dataPara['token'] = token;
        }
        form.append('token', dataPara['token'])
        form.append("file", param)
        return this.http.post(urls, form, {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        })
    }
    postFileAndData(url, data = null, param = null, config = null) {
        let urls = url
        const form = new FormData();
        let dataPara = {}
        dataPara = {
            ...(data || {}),
        }
        const token = localStorage.getItem('token');
        if (token) {
            dataPara['token'] = token;
        }
        if(data){
            for (let dataKey in data) {
                form.append(dataKey, data[dataKey])
            }
        }
        form.append('token', dataPara['token'])
        form.append("file", param)
        return this.http.post(urls, form, {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        })
    }
    put(url, data = null, param = null, config = null) {
        if (param) {
            url = this.parseUrl(url, param);
        }
        return this.http.put(url, data, config);
    }
    postFiles(url, data = null, param = null) {
        let urls = url
        const form = new FormData();
        let dataPara = {}
        dataPara = {
            ...(data || {}),
        }
        const token = localStorage.getItem('token');
        if (token) {
            dataPara['token'] = token;
        }
        // form.append('token', dataPara['token'])
        form.append('token', '1')
        form.append("file", param)
        return this.http.post(urls, form, {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        })
    }

}

export {
    Http
};
